// BookEditPanel.tsx
// NOTE: Helper logic verified via frontend/src/__tests__/BookEditPanel.test.tsx. Run
// `../scripts/test-frontend.sh BookEditPanel` when changing computed helpers.
import React, {useState, useEffect, ReactNode} from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import { message } from 'antd';
import axios from 'axios';
import { Book, Chapter, ChapterWordCounts, CreditBalance, SeriesSummary, FrenchNarrativeTone, TranslationProgressMap, RecentExportEntry } from './types';
import MetadataEditForm from './MetadataEditForm';
import type { AskAIInvocationPayload } from './AskAIModal';
import { EditBook } from './types';
import type { CreditTopUpContext } from './types/creditTopUp';
import CoverGenerationModal from './CoverGenerationModal';
import CoverHistoryModal from './CoverHistoryModal';
import CoverVariationsModal from './CoverVariationsModal';
import type { TranslationMilestoneTrackerProps } from './components/TranslationMilestoneTracker';
import { getTargetLanguageFromPair, splitLanguagePair } from './utils/languagePairs';
import { detectImageMimeFromBlob } from './utils/imageMime';
import MetadataCoverSection from './metadata/MetadataCoverSection';

interface AudiobookCreationOptions {
  version: string;
  voice: string;
  selectedChapters: string[];
  introText: string;
  outroText: string;
}

interface BookEditPanelProps {
  book: Book;
  editBook: EditBook;
  handleInputChange: (key: keyof EditBook, value: string | number | null | undefined, options?: { markDirty?: boolean }) => void;
  handleSave: () => void;
  blurb: string;
  setBlurb: React.Dispatch<React.SetStateAction<string>>;
  shouldSuppressBlurbDirty?: () => boolean;
  languages: string[];
  bookLanguage: string;
  setBookLanguage: React.Dispatch<React.SetStateAction<string>>;
  genres: string[];
  setGenres: React.Dispatch<React.SetStateAction<string[]>>;
  tropes: string[];
  setTropes: React.Dispatch<React.SetStateAction<string[]>>;
  keywords: string[];
  setKeywords: React.Dispatch<React.SetStateAction<string[]>>;
  intendedAudience?: string[];
  setIntendedAudience?: React.Dispatch<React.SetStateAction<string[]>>;
  toggleMetadataTranslateModal: (preferredTargetLanguage?: string) => void;
  toggleKeywordSuggestionModal: (preferredTargetLanguage?: string) => void;
  exportBlurbToTxt: () => void;
  handleGenerateBlurb: () => void;
  isTitleValid: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  blurbActiveTabKey: string;
  setBlurbActiveTabKey: React.Dispatch<React.SetStateAction<string>>;
  setCurrentLanguagePair: React.Dispatch<React.SetStateAction<string>>;
  setIsTranslatedTabActive: React.Dispatch<React.SetStateAction<boolean>>;
  isTranslatedTabActive: boolean;
  blurbTranslations: { [key: string]: string };
  titleTranslations: { [key: string]: string };
  seriesTranslations: { [key: string]: string };
  seriesDescriptionTranslations?: { [key: string]: string };
  keywordTranslations: { [key: string]: string[] };
  neverTranslateWords: { [key: string]: string[] };
  alwaysTranslateWords: { [key: string]: string[] };
  accountNeverTranslateWords?: { [key: string]: string[] };
  accountAlwaysTranslateWords?: { [key: string]: string[] };
  setNeverTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAlwaysTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAccountNeverTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAccountAlwaysTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setSeriesTranslateRules?: React.Dispatch<
    React.SetStateAction<{
      alwaysTranslate: Record<string, string[]>;
      neverTranslate: Record<string, string[]>;
    }>
  >;
  seriesNeverTranslateWords: { [key: string]: string[] };
  seriesAlwaysTranslateWords: { [key: string]: string[] };
  bookId: string;
  auth: any;
  seriesOptions: SeriesSummary[];
  selectedSeriesId: string | null;
  onSeriesChange: (value: string | null) => void;
  seriesDescription: string;
  onSeriesDescriptionChange: (value: string) => void;
  seriesInheritance: Record<string, boolean>;
  onSeriesInheritanceChange: (field: string, inherit: boolean) => void;
  onCreateSeries: (name: string, description: string) => Promise<void> | void;
  onCopyMetadata: (sourceBookId: string, fields: string[], applyToSeries: boolean) => Promise<void> | void;
  isSeriesBusy: boolean;
  creditBalance?: CreditBalance | null;
  fetchCreditBalance: (userId: string) => Promise<void>;
  handleDeleteTranslatedChapters: (sourceLang: string, targetLang: string) => void;
  handleDeleteProofreadChapters: (sourceLang: string, targetLang: string) => void;
  setIsNeverTranslateModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAlwaysTranslateModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setTitleTranslations: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  setSeriesTranslations: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  setBlurbTranslations: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  onFrenchNarrativeToneSaved?: (tone: FrenchNarrativeTone | undefined) => void;
  selectedChapter: Chapter | null;
  handleAddLanguage: () => void;
  commonGenres: string[];
  handleTranslateBookClick: () => void;
  handleProofreadBookClick: () => void;
  handleExportBookClick: () => void;
  recentExports?: RecentExportEntry[];
  onDownloadRecentExport?: (entry: RecentExportEntry) => Promise<void>;
  onRefreshProgress?: () => void;
  isRefreshingProgress?: boolean;
  toggleAudiobookModal: () => void;
  navigateToAudiobookEdit: () => void;
  handleCoverUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveCover: () => void;
  setHumanReviewModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  showRemoveCover: boolean;
  setShowRemoveCover: React.Dispatch<React.SetStateAction<boolean>>;
  audiobookVersionOptions?: Array<{
    value: string;
    label: string;
  }>;
  onTranslateText?: (text: string, targetLang: string) => Promise<string>;  
  isLangTesterUser: boolean;
  isBetaUser: boolean;
  chapterWordCounts: ChapterWordCounts
  languageCovers?: { [language: string]: string };
  selectedTab: string;
  onUpdateLanguageCovers: (covers: { [language: string]: string | null }) => void;  
  onCoverGenerationStart?: (language: string) => void;
  onCoverGenerationComplete?: (
    images: string[],
    options?: {
      mode: 'modify' | 'generate';
      replaceTitle?: boolean;
      replaceSeries?: boolean;
      addLanguageSticker?: boolean;
      authorMode?: 'include' | 'omit';
      originalTitle?: string;
      translatedTitle?: string;
      originalSeries?: string;
      translatedSeries?: string;
      language?: string;
      requestedCount?: number;
      generatedCount?: number;
      failureMessages?: string[];
      partialFailure?: boolean;
    }
  ) => void;  
  onTabChange?: (value: string) => void;
  openCoverGenerationSignal?: { language: string; ts: number };
  openCoverHistorySignal?: { language: string; ts: number };
  onOpenCoverTranslationForLanguage?: (language: string) => void;
  onOpenCoverHistoryForLanguage?: (language: string) => void;
  onActiveTabChange?: (tabKey: string) => void;
  onMetadataLanguageChange?: (language: string) => void;
  startAudiobookTourSignal?: number;
  onAudiobookTopUpCredits?: (context: CreditTopUpContext) => void;
  startCharactersTourSignal?: number;
  onOpenChapterUploadModal: () => void;
  isChapterUploadDisabled?: boolean;
  chapterUploadDisabledReason?: string;
  relationshipSummaryCard?: ReactNode;
  relationshipFreeRunAvailable?: boolean;
  relationshipAnalysisLoading?: boolean;
  registryRefreshSignal?: number | null;
  registryClearSignal?: number | null;
  resolvedUserId: string;
  glossaryScanTrigger?: { pair: string; nonce: number } | null;
  termScanReviewTrigger?: { pair: string; nonce: number } | null;
  onOpenRelationshipEditor: (chapterGuid?: string) => void;
  onOpenRelationshipAnalysis: () => void;
  onCloseRelationshipEditor?: () => void;
  onCloseRelationshipAnalysis?: () => void;
  metadataTabSignal?: { tab: string; ts: number } | null;
  metadataLanguageSignal?: { language: string; ts: number } | null;
  translationTrackerData?: TranslationMilestoneTrackerProps | null;
  translationProgress?: TranslationProgressMap;
  translationLanguages?: string[];
  showTranslationProgress?: boolean;
  onSelectTranslationLanguage?: (language: string) => void;
  ensureMetadataLanguage?: (language: string) => Promise<boolean>;
  onPrepareMetadataTranslateLanguage?: (language: string) => void;
  onPrepareKeywordSuggestionLanguage?: (language: string) => void;
  onOpenProofreadForLanguage?: (language: string) => void;
  onApplyPendingProofreadReviewsForLanguage?: (language: string) => void;
  applyingPendingProofreadLanguage?: string | null;
  onTermScanTopUpCredits?: (context: CreditTopUpContext) => void;
  onRunMetadataAutofill?: (options?: {
    mode?: 'new_upload' | 'existing_suggestions';
    applyMode?: 'empty_fields' | 'suggestions_only';
    forceRegenerate?: boolean;
    applyFields?: string[];
    applyValues?: Record<string, string[]>;
  }) => Promise<any> | any;
  frenchDialogueLegacyEligibilityByPair?: Record<
    string,
    {
      eligible: boolean;
      reasonCode: string;
      pair?: string;
      alreadyRan?: boolean;
      inProgress?: boolean;
      rulesetVersion?: string;
    }
  >;
  onFetchFrenchDialogueLegacyEligibility?: (params: {
    pair: string;
    targetLanguage: string;
    force?: boolean;
  }) => Promise<
    | {
        eligible: boolean;
        reasonCode: string;
        pair?: string;
        alreadyRan?: boolean;
        inProgress?: boolean;
        rulesetVersion?: string;
      }
    | null
  >;
  onRunFrenchDialogueLegacyCorrection?: (params: {
    pair: string;
    targetLanguage: string;
    chapterGuid?: string;
    chapterName?: string;
  }) => void;
  isAdmin?: boolean;
  onAskAI?: (payload: AskAIInvocationPayload) => void;
}

export const resolveTranslationForTab = (
  selectedTab: string,
  translations: Record<string, string>,
  sourceLanguage?: string
): string | null => {
  if (selectedTab === 'Original') return null;
  const { sourceLang: src, targetLang: tgt } = splitLanguagePair(selectedTab);
  const candidates = [
    selectedTab.replace('-', '_'),
    selectedTab,
    sourceLanguage && tgt ? `${sourceLanguage}_${tgt}` : null,
  ].filter(Boolean) as string[];
  for (const key of candidates) {
    const value = translations[key];
    if (value) {
      return value;
    }
  }
  return null;
};

export const calculateCoverCreditCost = ({
  mode,
  imageCount,
}: {
  mode: 'modify' | 'generate';
  imageCount: number;
}): number => {
  const costPerImage = mode === 'generate' ? 750 : 500;
  return Math.max(0, imageCount) * costPerImage;
};

export type CoverDisplaySource = 'none' | 'original' | 'translated' | 'translated_placeholder_original';

export const resolveCoverDisplayState = ({
  selectedTab,
  selectedTargetLanguage,
  translatedCover,
  languageCovers,
  bookCoverUrl,
  originalLanguage,
}: {
  selectedTab: string;
  selectedTargetLanguage: string;
  translatedCover?: string;
  languageCovers?: { [language: string]: string };
  bookCoverUrl?: string;
  originalLanguage?: string;
}): {
  displayCoverUrl: string;
  source: CoverDisplaySource;
  indicatorLabel: string | null;
} => {
  const originalLanguageLabel = (originalLanguage || 'Original').trim() || 'Original';
  const targetLanguageLabel = (selectedTargetLanguage || '').trim();
  const originalCover = languageCovers?.['original'];
  const originalCoverCandidate =
    originalCover === '' ? '' : (originalCover || bookCoverUrl || '');

  if (selectedTab === 'Original') {
    if (!originalCoverCandidate) {
      return { displayCoverUrl: '', source: 'none', indicatorLabel: null };
    }
    return {
      displayCoverUrl: originalCoverCandidate,
      source: 'original',
      indicatorLabel: `${originalLanguageLabel} cover`,
    };
  }

  if (targetLanguageLabel) {
    if (translatedCover === '') {
      return { displayCoverUrl: '', source: 'none', indicatorLabel: null };
    }
    if (translatedCover) {
      return {
        displayCoverUrl: translatedCover,
        source: 'translated',
        indicatorLabel: `${targetLanguageLabel} cover`,
      };
    }
    if (originalCoverCandidate) {
      return {
        displayCoverUrl: originalCoverCandidate,
        source: 'translated_placeholder_original',
        indicatorLabel: `${targetLanguageLabel} cover placeholder (using ${originalLanguageLabel} cover)`,
      };
    }
    return { displayCoverUrl: '', source: 'none', indicatorLabel: null };
  }

  if (!originalCoverCandidate) {
    return { displayCoverUrl: '', source: 'none', indicatorLabel: null };
  }
  return {
    displayCoverUrl: originalCoverCandidate,
    source: 'original',
    indicatorLabel: `${originalLanguageLabel} cover`,
  };
};

const BookEditPanel: React.FC<BookEditPanelProps> = ({
  book,
  editBook,
  handleInputChange,
  handleSave,
  blurb,
  setBlurb,
  shouldSuppressBlurbDirty,
  languages,
  bookLanguage,
  setBookLanguage,
  genres,
  setGenres,
  tropes,
  setTropes,
  keywords,
  intendedAudience,
  setIntendedAudience,
  setKeywords,
  toggleMetadataTranslateModal,
  toggleKeywordSuggestionModal,
  exportBlurbToTxt,
  handleGenerateBlurb,
  isTitleValid,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  blurbActiveTabKey,
  setBlurbActiveTabKey,
  setCurrentLanguagePair,
  setIsTranslatedTabActive,
  isTranslatedTabActive,
  blurbTranslations,
  titleTranslations,
  seriesTranslations,
  seriesDescriptionTranslations = {},
  keywordTranslations,
  neverTranslateWords,
  alwaysTranslateWords,
  accountNeverTranslateWords,
  accountAlwaysTranslateWords,
  setNeverTranslateWords,
  setAlwaysTranslateWords,
  setAccountNeverTranslateWords,
  setAccountAlwaysTranslateWords,
  setSeriesTranslateRules,
  seriesNeverTranslateWords,
  seriesAlwaysTranslateWords,
  bookId,
  auth,
  seriesOptions,
  selectedSeriesId,
  onSeriesChange,
  seriesDescription,
  onSeriesDescriptionChange,
  seriesInheritance,
  onSeriesInheritanceChange,
  onCreateSeries,
  onCopyMetadata,
  isSeriesBusy,
  creditBalance,
  fetchCreditBalance,
  handleDeleteTranslatedChapters,
  handleDeleteProofreadChapters,
  setIsNeverTranslateModalVisible,
  setIsAlwaysTranslateModalVisible,
  setTitleTranslations,
  setSeriesTranslations,
  setBlurbTranslations,
  onFrenchNarrativeToneSaved,
  selectedChapter,
  handleAddLanguage,
  commonGenres,
  handleTranslateBookClick,
  handleProofreadBookClick,
  handleExportBookClick,
  recentExports = [],
  onDownloadRecentExport = async () => {},
  onRefreshProgress,
  isRefreshingProgress = false,
  toggleAudiobookModal,
  navigateToAudiobookEdit,
  handleCoverUpload,
  handleRemoveCover,
  showRemoveCover,
  setShowRemoveCover,
  audiobookVersionOptions,
  onTranslateText,
  isLangTesterUser,
  isBetaUser,
  chapterWordCounts,
  languageCovers,
  onUpdateLanguageCovers,
  onCoverGenerationStart,
  onCoverGenerationComplete,
  selectedTab,
  onTabChange,
  openCoverGenerationSignal,
  openCoverHistorySignal,
  onOpenCoverTranslationForLanguage,
  onOpenCoverHistoryForLanguage,
  onActiveTabChange,
  onMetadataLanguageChange,
  startAudiobookTourSignal,
  onAudiobookTopUpCredits,
  startCharactersTourSignal,
  onOpenChapterUploadModal,
  isChapterUploadDisabled = false,
  chapterUploadDisabledReason,
  relationshipSummaryCard,
  relationshipFreeRunAvailable,
  relationshipAnalysisLoading,
  registryRefreshSignal,
  registryClearSignal,
  resolvedUserId,
  glossaryScanTrigger,
  termScanReviewTrigger,
  onOpenRelationshipEditor,
  onOpenRelationshipAnalysis,
  onCloseRelationshipEditor,
  onCloseRelationshipAnalysis,
  metadataTabSignal,
  metadataLanguageSignal,
  translationTrackerData,
  translationProgress,
  translationLanguages,
  showTranslationProgress,
  onSelectTranslationLanguage,
  ensureMetadataLanguage,
  onPrepareMetadataTranslateLanguage,
  onPrepareKeywordSuggestionLanguage,
  onOpenProofreadForLanguage,
  onApplyPendingProofreadReviewsForLanguage,
  applyingPendingProofreadLanguage,
  onTermScanTopUpCredits,
  onRunMetadataAutofill,
  frenchDialogueLegacyEligibilityByPair,
  onFetchFrenchDialogueLegacyEligibility,
  onRunFrenchDialogueLegacyCorrection,
  isAdmin = false,
  onAskAI,
}) => {
const [coverGenerationModalVisible, setCoverGenerationModalVisible] = useState(false);
const [coverGenerationLanguage, setCoverGenerationLanguage] = useState('');
const [historyOpen, setHistoryOpen] = useState(false);
const [historyTargetLanguage, setHistoryTargetLanguage] = useState<string | null>(null);
const [variationsVisible, setVariationsVisible] = useState(false);
const [variationsBaseImage, setVariationsBaseImage] = useState<string>('');

// Centralized API base for all requests in this file
const API_BASE = resolveFrontendApiBase();

useEffect(() => {
  if (selectedTab && selectedTab !== 'Original') {
    setCoverGenerationLanguage(getTargetLanguageFromPair(selectedTab) || '');
  } else {
    setCoverGenerationLanguage('Original');
  }
}, [selectedTab]);

const selectedTargetLanguage = (() => {
  if (!selectedTab || selectedTab === 'Original') return '';
  return getTargetLanguageFromPair(selectedTab) || '';
})();
const originalLanguageLabel = (bookLanguage || 'Original').trim() || 'Original';
const selectedTargetLanguageKey = selectedTargetLanguage.toLowerCase();
const translatedCover =
  selectedTargetLanguage
    ? (languageCovers?.[selectedTargetLanguage] ?? languageCovers?.[selectedTargetLanguageKey])
    : undefined;

// Ensure balance is fetched whenever the modal becomes visible
useEffect(() => {
  if (coverGenerationModalVisible) {
    const uid = auth?.currentUser?.uid;
    if (uid) {
      fetchCreditBalance(uid).catch(() => {});
    }
  }
}, [coverGenerationModalVisible, auth?.currentUser?.uid, fetchCreditBalance]);

// Ensure balance is fetched whenever the variations modal becomes visible
useEffect(() => {
  if (variationsVisible) {
    const uid = auth?.currentUser?.uid;
    if (uid) {
      fetchCreditBalance(uid).catch(() => {});
    }
  }
}, [variationsVisible, auth?.currentUser?.uid, fetchCreditBalance]);

// Open CoverGenerationModal when parent requests a regenerate
useEffect(() => {
  if (openCoverGenerationSignal?.language) {
    setCoverGenerationLanguage(openCoverGenerationSignal.language || 'Original');
    setCoverGenerationModalVisible(true);
  }
}, [openCoverGenerationSignal?.ts]);

useEffect(() => {
  if (openCoverHistorySignal?.language) {
    setHistoryTargetLanguage(openCoverHistorySignal.language || 'Original');
    setHistoryOpen(true);
  }
}, [openCoverHistorySignal?.ts]);

const coverDisplayState = resolveCoverDisplayState({
  selectedTab,
  selectedTargetLanguage,
  translatedCover,
  languageCovers,
  bookCoverUrl: book.coverUrl || '',
  originalLanguage: bookLanguage,
});
const displayCoverUrl = coverDisplayState.displayCoverUrl;
	
const getTranslatedTitle = () => {
  if (selectedTab === 'Original') return book.title;
  const translation = resolveTranslationForTab(selectedTab, titleTranslations, bookLanguage);
  return translation || `[${book.title}]`;
};

const getTranslatedSeriesName = () => {
  if (!book.series || selectedTab === 'Original') return book.series || '';
  const translation = resolveTranslationForTab(selectedTab, seriesTranslations, bookLanguage);
  return translation || `[${book.series}]`;
};

  const getCoverCreditCost = calculateCoverCreditCost;

  const sanitizeFilename = (s: string) =>
    s.replace(/[^a-zA-Z0-9\-_. ]/g, '').trim().replace(/\s+/g, '_');

  const handleDownloadCover = async () => {
    const url = displayCoverUrl;
    if (!url) return;

    const langLabel = selectedTab === 'Original'
      ? originalLanguageLabel
      : (getTargetLanguageFromPair(selectedTab) || 'Translated');

    const baseFilename = `${sanitizeFilename(book.title || 'cover')}_${langLabel}_cover`;

    const triggerDownload = (href: string, filename: string) => {
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    const downloadBlob = (blob: Blob, filename: string) => {
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, filename);
      URL.revokeObjectURL(objectUrl);
    };

    const convertBlobToJpeg = (blob: Blob): Promise<Blob> => new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(blob);
      const image = new Image();
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
          const context = canvas.getContext('2d');
          if (!context) throw new Error('JPEG conversion is unavailable');
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0);
          canvas.toBlob((jpegBlob) => {
            URL.revokeObjectURL(objectUrl);
            if (jpegBlob) resolve(jpegBlob);
            else reject(new Error('JPEG conversion failed'));
          }, 'image/jpeg', 0.95);
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          reject(error);
        }
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Cover image could not be decoded'));
      };
      image.src = objectUrl;
    });

    // API base helper no longer needed; use API_BASE constant

    try {
      // Path A: Data URL -> direct decode
      if (url.startsWith('data:')) {
        let mime = 'image/png';
        const [header, base64] = url.split(',');
        const match = header.match(/data:(.*?);base64/);
        if (match) mime = match[1];

        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: mime });

        const jpegBlob = await convertBlobToJpeg(blob);
        downloadBlob(jpegBlob, `${baseFilename}.jpg`);
        return;
      }

      // Ask the backend to decode and re-encode the source. Changing only a
      // filename extension would leave PNG bytes in a file named .jpg, which
      // storefront upload validators correctly reject.
      try {
        const proxyRes = await fetch(`${API_BASE}/api/proxy-download`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url, filename: baseFilename, output_format: 'jpeg' }),
        });
        if (!proxyRes.ok) {
          const msg = await proxyRes.text().catch(() => '');
          throw new Error(`JPEG download failed: ${proxyRes.status} ${msg}`);
        }
        const blob = await proxyRes.blob();
        const detectedMime = await detectImageMimeFromBlob(blob, proxyRes.headers.get('content-type') || '');
        if (detectedMime !== 'image/jpeg') {
          throw new Error(`Expected JPEG data, received ${detectedMime || 'an unknown format'}`);
        }
        downloadBlob(blob, `${baseFilename}.jpg`);
        return;
      } catch (_e) {
        // Continue to a browser-side conversion when the source allows CORS.
      }

      try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const jpegBlob = await convertBlobToJpeg(blob);
        downloadBlob(jpegBlob, `${baseFilename}.jpg`);
        return;
      } catch (_e) {
        throw new Error('Cover could not be converted to JPEG');
      }
    } catch (e) {
      console.error('Failed to download cover', e);
      message.error('Failed to download cover as JPEG');
    }
  };

  // Convert an HTTP(S) URL to a data URL for use as CoverVariationsModal baseImage
  const convertUrlToDataUrl = async (url: string): Promise<string> => {
    if (url.startsWith('data:')) return url;
    // Try direct CORS fetch first
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const reader = new FileReader();
      const p: Promise<string> = new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
      });
      reader.readAsDataURL(blob);
      return await p;
    } catch (_e) {
      // Fall back to backend proxy if direct fetch fails
      try {
        const proxyRes = await fetch(`${API_BASE}/api/proxy-download`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url, filename: 'cover.png' }),
        });
        if (!proxyRes.ok) throw new Error(`Proxy failed: ${proxyRes.status}`);
        const blob = await proxyRes.blob();
        const reader = new FileReader();
        const p: Promise<string> = new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
        });
        reader.readAsDataURL(blob);
        return await p;
      } catch (e) {
        console.error('Failed to convert URL to data URL', e);
        throw e;
      }
    }
  };

  const handleRemoveDisplayedCover = async () => {
    try {
      const uid = auth?.currentUser?.uid;
      if (!uid) {
        message.error('No user logged in');
        return;
      }
      if (selectedTab === 'Original') {
        handleRemoveCover();
        onUpdateLanguageCovers && onUpdateLanguageCovers({ original: '' });
        message.success(`${originalLanguageLabel} cover removed`);
        return;
      }
      if (selectedTargetLanguage) {
        const targetLang = selectedTargetLanguage.toLowerCase();
        if (!translatedCover) {
          onUpdateLanguageCovers && onUpdateLanguageCovers({ [targetLang]: '' });
          message.success(`Ready to upload a new cover for ${selectedTargetLanguage}`);
          return;
        }
        await axios.delete(`${API_BASE}/api/book/${bookId}/cover/${encodeURIComponent(targetLang)}`, {
          params: { user_id: uid }
        });
        onUpdateLanguageCovers && onUpdateLanguageCovers({ [targetLang]: '' });
        message.success(`Cover removed for ${selectedTargetLanguage}`);
      } else {
        handleRemoveCover();
        onUpdateLanguageCovers && onUpdateLanguageCovers({ original: '' });
        message.success('Cover removed');
      }
    } catch (err) {
      console.error('Error removing cover', err);
      if (axios.isAxiosError(err) && err.response?.status === 404 && selectedTargetLanguage) {
        const targetLang = selectedTargetLanguage.toLowerCase();
        onUpdateLanguageCovers && onUpdateLanguageCovers({ [targetLang]: '' });
        message.success(`Ready to upload a new cover for ${selectedTargetLanguage}`);
        return;
      }
      const detail = axios.isAxiosError(err)
        ? String(err.response?.data?.detail || err.response?.data?.message || '').trim()
        : '';
      message.error(detail ? `Failed to remove cover: ${detail}` : 'Failed to remove cover');
    }
  };

  const isTranslateCoverContext = selectedTab !== 'Original';
  const coverTargetLanguage = isTranslateCoverContext ? selectedTargetLanguage : '';
  const isHebrewCoverTranslationDisabled = isTranslateCoverContext && coverTargetLanguage.trim().toLowerCase() === 'hebrew';
  const coverSection = (
    <MetadataCoverSection
      displayCoverUrl={displayCoverUrl}
      indicatorLabel={coverDisplayState.indicatorLabel}
      coverDisplaySource={coverDisplayState.source}
      actionLabel={selectedTab === 'Original' ? 'Generate Cover' : 'Translate Cover'}
      isCoverActionDisabled={isHebrewCoverTranslationDisabled}
      disabledActionReason="Cover translation is not available for Hebrew at this time."
      showRemoveCover={showRemoveCover}
      setShowRemoveCover={setShowRemoveCover}
      onDownloadCover={handleDownloadCover}
      onRemoveCover={handleRemoveDisplayedCover}
      onCoverUpload={handleCoverUpload}
      onOpenCoverGeneration={() => setCoverGenerationModalVisible(true)}
      onOpenCoverHistory={() => {
        setHistoryTargetLanguage(null);
        setHistoryOpen(true);
      }}
    />
  );

  return (
    <div className="book-edit-workspace" data-tour-id="metadata-form" data-testid="tutorial:metadata:workspace">
	  
      {/* Metadata Fields */}
      <MetadataEditForm
        selectedTab={selectedTab}
        book={book}
        editBook={editBook}
        handleInputChange={handleInputChange}
        handleSave={handleSave}
        blurb={blurb}
        setBlurb={setBlurb}
        shouldSuppressBlurbDirty={shouldSuppressBlurbDirty}
        languages={languages}
        bookLanguage={bookLanguage}
        setBookLanguage={setBookLanguage}
        genres={genres}
        setGenres={setGenres}
        tropes={tropes}
        setTropes={setTropes}
        keywords={keywords}
        setKeywords={setKeywords}
        intendedAudience={intendedAudience || []}
        setIntendedAudience={val => setIntendedAudience && setIntendedAudience(val)}
        toggleMetadataTranslateModal={toggleMetadataTranslateModal}
        toggleKeywordSuggestionModal={toggleKeywordSuggestionModal}
        exportBlurbToTxt={exportBlurbToTxt}
        handleGenerateBlurb={handleGenerateBlurb}
        isTitleValid={isTitleValid}
        hasUnsavedChanges={hasUnsavedChanges}
        setHasUnsavedChanges={setHasUnsavedChanges}
        blurbActiveTabKey={blurbActiveTabKey}
        setBlurbActiveTabKey={setBlurbActiveTabKey}
        setCurrentLanguagePair={setCurrentLanguagePair}
        setIsTranslatedTabActive={setIsTranslatedTabActive}
        isTranslatedTabActive={isTranslatedTabActive}
        blurbTranslations={blurbTranslations}
        titleTranslations={titleTranslations}
        seriesTranslations={seriesTranslations}
        seriesDescriptionTranslations={seriesDescriptionTranslations}
        keywordTranslations={keywordTranslations}
        neverTranslateWords={neverTranslateWords}
        alwaysTranslateWords={alwaysTranslateWords}
        accountNeverTranslateWords={accountNeverTranslateWords}
        accountAlwaysTranslateWords={accountAlwaysTranslateWords}
        setNeverTranslateWords={setNeverTranslateWords}
        setAlwaysTranslateWords={setAlwaysTranslateWords}
        setAccountNeverTranslateWords={setAccountNeverTranslateWords}
        setAccountAlwaysTranslateWords={setAccountAlwaysTranslateWords}
        setSeriesTranslateRules={setSeriesTranslateRules}
        seriesNeverTranslateWords={seriesNeverTranslateWords}
        seriesAlwaysTranslateWords={seriesAlwaysTranslateWords}
        bookId={bookId}
        auth={auth}
        seriesOptions={seriesOptions}
        selectedSeriesId={selectedSeriesId}
        onSeriesChange={onSeriesChange}
        seriesDescription={seriesDescription}
        onSeriesDescriptionChange={onSeriesDescriptionChange}
        seriesInheritance={seriesInheritance}
        onSeriesInheritanceChange={onSeriesInheritanceChange}
        onCreateSeries={onCreateSeries}
        onCopyMetadata={onCopyMetadata}
        isSeriesBusy={isSeriesBusy}
        handleDeleteTranslatedChapters={handleDeleteTranslatedChapters}
        handleDeleteProofreadChapters={handleDeleteProofreadChapters}
        setIsNeverTranslateModalVisible={setIsNeverTranslateModalVisible}
        setIsAlwaysTranslateModalVisible={setIsAlwaysTranslateModalVisible}
        setTitleTranslations={setTitleTranslations}
        setSeriesTranslations={setSeriesTranslations}
        setBlurbTranslations={setBlurbTranslations}
        onFrenchToneSaved={onFrenchNarrativeToneSaved}
        selectedChapter={selectedChapter}
        handleAddLanguage={handleAddLanguage}
        commonGenres={commonGenres}
        handleTranslateBookClick={handleTranslateBookClick}
        handleProofreadBookClick={handleProofreadBookClick}
        handleExportBookClick={handleExportBookClick}
        recentExports={recentExports}
        onDownloadRecentExport={onDownloadRecentExport}
        onRefreshProgress={onRefreshProgress}
        isRefreshingProgress={isRefreshingProgress}
        toggleAudiobookModal={toggleAudiobookModal}
		audiobookVersionOptions={audiobookVersionOptions}
		onTranslateText={onTranslateText}	
		isLangTesterUser={isLangTesterUser}
		isBetaUser={isBetaUser}
		chapterWordCounts={chapterWordCounts}
			onTabChange={onTabChange}
			onActiveTabChange={onActiveTabChange}
			onMetadataLanguageChange={onMetadataLanguageChange}
			startAudiobookTourSignal={startAudiobookTourSignal}
			onAudiobookTopUpCredits={onAudiobookTopUpCredits}
			startCharactersTourSignal={startCharactersTourSignal}
			languageCovers={languageCovers}
		relationshipSummaryCard={relationshipSummaryCard}
		relationshipFreeRunAvailable={relationshipFreeRunAvailable}
		relationshipAnalysisLoading={relationshipAnalysisLoading}
		registryRefreshSignal={registryRefreshSignal}
		registryClearSignal={registryClearSignal}
		apiBase={API_BASE}
		resolvedUserId={resolvedUserId}
		glossaryScanTrigger={glossaryScanTrigger}
		termScanReviewTrigger={termScanReviewTrigger}
		onOpenRelationshipEditor={onOpenRelationshipEditor}
		onOpenRelationshipAnalysis={onOpenRelationshipAnalysis}
		onCloseRelationshipEditor={onCloseRelationshipEditor}
		onCloseRelationshipAnalysis={onCloseRelationshipAnalysis}
		activeTabSignal={metadataTabSignal}
		metadataLanguageSignal={metadataLanguageSignal}
        translationTrackerData={translationTrackerData}
        translationProgress={translationProgress}
        translationLanguages={translationLanguages}
        showTranslationProgress={showTranslationProgress}
        onSelectTranslationLanguage={onSelectTranslationLanguage}
        ensureMetadataLanguage={ensureMetadataLanguage}
        onPrepareMetadataTranslateLanguage={onPrepareMetadataTranslateLanguage}
        onPrepareKeywordSuggestionLanguage={onPrepareKeywordSuggestionLanguage}
        onOpenProofreadForLanguage={onOpenProofreadForLanguage}
        onApplyPendingProofreadReviewsForLanguage={onApplyPendingProofreadReviewsForLanguage}
        applyingPendingProofreadLanguage={applyingPendingProofreadLanguage}
        onTermScanTopUpCredits={onTermScanTopUpCredits}
        onRunMetadataAutofill={onRunMetadataAutofill}
        frenchDialogueLegacyEligibilityByPair={frenchDialogueLegacyEligibilityByPair}
        onFetchFrenchDialogueLegacyEligibility={onFetchFrenchDialogueLegacyEligibility}
        onRunFrenchDialogueLegacyCorrection={onRunFrenchDialogueLegacyCorrection}
        onOpenChapterUploadModal={onOpenChapterUploadModal}
        isChapterUploadDisabled={isChapterUploadDisabled}
        chapterUploadDisabledReason={chapterUploadDisabledReason}
        onOpenCoverTranslationForLanguage={onOpenCoverTranslationForLanguage}
        onOpenCoverHistoryForLanguage={onOpenCoverHistoryForLanguage}
        handleCoverUpload={handleCoverUpload}
        isAdmin={isAdmin}
        onAskAI={onAskAI}
        creditBalance={creditBalance}
        fetchCreditBalance={fetchCreditBalance}
        coverSection={coverSection}
	/>
<CoverGenerationModal
  visible={coverGenerationModalVisible}
  onClose={() => setCoverGenerationModalVisible(false)}
  bookTitle={book.title}
  translatedTitle={getTranslatedTitle()}  // Use the existing function
  seriesName={book.series}
  translatedSeriesName={getTranslatedSeriesName()}  // Use the existing function
  targetLanguage={coverGenerationLanguage || 'Original'}
  bookLanguageName={bookLanguage}
  currentCoverUrl={languageCovers?.['original'] === '' ? '' : (languageCovers?.['original'] || book.coverUrl)}
  bookId={bookId}
  userId={auth?.currentUser?.uid || ''}
  creditBalance={creditBalance}
  getCoverCreditCost={getCoverCreditCost}
  onGenerationStart={(language) => onCoverGenerationStart?.(language)}
  onGenerationComplete={onCoverGenerationComplete || (() => {})}
  authorName={book.author}
  genre={(genres && genres.length ? genres[0] : (book.genres && book.genres.length ? book.genres[0] : ''))}
  tropes={tropes}
  keywords={keywords}
  bookNumber={book.booknumber}
  availableLanguages={[...new Set(['Original', ...languages])]} 
  getMetadataForLanguage={(language) => {
    if (language === 'Original') return { title: '', series: '' };
    const src = bookLanguage;
    const tgt = language;
    const k1 = `${src}_${tgt}`;
    const k2 = `${src}-${tgt}`;
    return {
      title: titleTranslations[k1] || titleTranslations[k2] || '',
      series: seriesTranslations[k1] || seriesTranslations[k2] || '',
    };
  }}
  isTranslationContext={selectedTab !== 'Original'}
/>

<CoverHistoryModal
  visible={historyOpen}
  onClose={() => {
    setHistoryOpen(false);
    setHistoryTargetLanguage(null);
  }}
  apiBase={API_BASE}
  bookId={bookId}
  userId={auth?.currentUser?.uid || ''}
  bookLanguageName={bookLanguage}
  targetLanguage={
    historyTargetLanguage || (selectedTab === 'Original'
      ? 'Original'
      : (selectedTargetLanguage || 'Original'))
  }
  bookTitle={book.title}
  onPromoted={(languageKey, url) => {
    // Update local cover mapping
    if (onUpdateLanguageCovers) {
      if (languageKey === 'original') {
        onUpdateLanguageCovers({ original: url });
      } else {
        onUpdateLanguageCovers({ [languageKey]: url });
      }
    }
  }}
  onOpenVariations={async (url: string) => {
    try {
      const dataUrl = await convertUrlToDataUrl(url);
      setVariationsBaseImage(dataUrl);
      setHistoryOpen(false);
      setVariationsVisible(true);
    } catch {
      message.error('Unable to open variations for this image.');
    }
  }}
/>

<CoverVariationsModal
  visible={variationsVisible}
  onClose={() => setVariationsVisible(false)}
  baseImage={variationsBaseImage}
  targetLanguage={coverGenerationModalVisible ? (coverGenerationLanguage || 'Original') : (selectedTab === 'Original'
    ? 'Original'
    : (selectedTargetLanguage || 'Original'))}
  bookId={bookId}
  userId={auth?.currentUser?.uid || ''}
  bookTitle={book.title}
  translatedTitle={getTranslatedTitle()}
  seriesName={book.series}
  translatedSeriesName={getTranslatedSeriesName()}
  authorName={book.author}
  creditBalance={creditBalance}
  getCoverCreditCost={getCoverCreditCost}
  onGenerated={(images: string[]) => { onCoverGenerationComplete?.(images); }}
  onStart={(language: string) => { onCoverGenerationStart?.(language); }}
  onComplete={(images: string[]) => { onCoverGenerationComplete?.(images); }}
/>
    </div>
  );
};

export default BookEditPanel;
